import React, { useEffect, useState } from "react";
import { X, Save } from "lucide-react";
import { toast } from "sonner";
import { updateMe } from "../../lib/communityApi";
import { extractApiErrorMessage, formatSgDateTime } from "../../lib/format";

const MAX_BIO = 280;

const ProfileDrawer = ({ open, onClose, token, me, onUpdated }) => {
  const [bio, setBio] = useState(me?.bio || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setBio(me?.bio || "");
  }, [open, me]);

  if (!open) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateMe(token, { bio: bio.slice(0, MAX_BIO) });
      toast.success("Profile saved.");
      if (typeof onUpdated === "function") onUpdated(updated);
      onClose();
    } catch (err) {
      console.error("updateMe failed:", err);
      toast.error(extractApiErrorMessage(err, "Could not save your profile."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-[#1f2a36]/40"
        onClick={onClose}
      />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl flex flex-col">
        <header className="flex items-center justify-between p-5 border-b border-[#eef2f6]">
          <h2 className="ht-display text-xl text-[#2c3e50]">Your profile</h2>
          <button
            onClick={onClose}
            className="text-[#9aa6b2] hover:text-[#2c3e50]"
            aria-label="Close profile"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-5">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#eaf4fb] text-[#3d6486] font-semibold ht-display text-xl">
              {(me?.first_name || "?").slice(0, 1).toUpperCase()}
            </span>
            <div>
              <p className="ht-display text-lg text-[#2c3e50]">
                {me?.first_name || "Friend"}
              </p>
              <p className="text-xs text-[#7e8a96]">{me?.email}</p>
              {me?.joined_at && (
                <p className="text-xs text-[#9aa6b2]">
                  Joined {formatSgDateTime(me.joined_at)}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <label className="ht-body text-sm font-semibold text-[#2c3e50]">
              A short bio (optional)
            </label>
            <p className="mt-1 text-xs text-[#7e8a96]">
              One or two lines so others know a bit about you. Visible to other
              members on your posts.
            </p>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, MAX_BIO))}
              rows={5}
              placeholder="e.g. Caregiver, runner, terrible at small talk."
              className="ht-input mt-2 w-full rounded-md border border-[#d6dde4] bg-white px-3.5 py-3 text-[15px] placeholder:text-[#9aa6b2] resize-y"
            />
            <p className="mt-1 text-[11px] text-[#9aa6b2] text-right">
              {bio.length}/{MAX_BIO}
            </p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="ht-btn mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#3d6486] hover:bg-[#345674] text-white font-semibold py-3 text-[15px] disabled:opacity-70"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : "Save profile"}
          </button>
        </form>
      </aside>
    </div>
  );
};

export default ProfileDrawer;
